/**
 * File: src/ui-component/backend-processes/BackendProcessControl.js
 * Layer: Shared UI
 * Description: Renders the global Backend Process header control, list, and
 * realtime socket-driven updates for long-running backend workflows.
 * Created: 2026-08-13
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { alpha, useTheme } from '@mui/material/styles';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Chip,
    ClickAwayListener,
    List,
    ListItemButton,
    Paper,
    Popper,
    Stack,
    Typography
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import { store, useDispatch, useSelector } from 'store';
import { fetchBackendProcesses, selectActiveBackendProcesses, upsertProcess } from 'store/slices/backend-process';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { releaseAppSocket, retainAppSocket } from 'utils/app-socket';
import { IconDeviceDesktopCog } from '@tabler/icons-react';
import { BACKEND_PROCESS_TYPES } from 'constants/background-process';

const styles = {
    triggerWrap: {
        ml: 1,
        mr: 1
    },
    panelCard: {
        width: 420,
        maxWidth: 'calc(100vw - 24px)'
    },
    panelHeader: {
        p: 2,
        pb: 1.5
    },
    panelBody: {
        maxHeight: 'calc(100vh - 220px)',
        overflowY: 'auto'
    },
    item: (theme, borderRadius, isActive) => ({
        alignItems: 'flex-start',
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: isActive ? alpha(theme.palette.secondary.main, 0.06) : theme.palette.background.paper,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05)
        },
        '&:last-of-type': {
            borderBottom: 'none',
            borderBottomLeftRadius: `${borderRadius}px`,
            borderBottomRightRadius: `${borderRadius}px`
        }
    }),
    itemHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1
    },
    itemBody: {
        mt: 0.5
    },
    emptyState: {
        px: 2,
        py: 4,
        textAlign: 'center'
    },
    triggerBadge: {
        '& .MuiBadge-badge': {
            right: 4,
            top: 4,
            minWidth: 18,
            height: 18,
            px: 0.5,
            fontSize: '0.65rem',
            fontWeight: 700
        }
    }
};

const ACTIVE_PROCESS_STATUSES = ['queued', 'processing'];

const getStatusChipColor = (status = '') => {
    if (status === 'completed') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'processing') return 'info';
    return 'warning';
};

const formatProcessDate = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return new Intl.DateTimeFormat('en', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    }).format(date);
};

const getAssessmentLink = (process = {}) => {
    const assessmentCode = String(process?.metadata?.assessmentCode || '').trim();
    return assessmentCode ? `/assessments/info/${assessmentCode}` : '';
};

const getCourseLink = (process = {}) => {
    const courseId = String(process?.metadata?.courseId || '').trim();
    return courseId ? `/courses/continue-draft/${courseId}/true` : '';
};

const isResubmissionGuidanceAssessmentProcess = (process = {}) =>
    process?.processType === BACKEND_PROCESS_TYPES.RESUBMISSION_GUIDANCE && process?.entityType === 'assessment';

const isInterviewTranscriptAssessmentProcess = (process = {}) =>
    process?.processType === BACKEND_PROCESS_TYPES.INTERVIEW_TRANSCRIPT && process?.entityType === 'assessment';

const getProcessNavigation = (process = {}) => {
    const assessmentLink = getAssessmentLink(process);

    if (process.processType === BACKEND_PROCESS_TYPES.EVIDENCE_PACKAGE && assessmentLink) {
        return { to: `${assessmentLink}?evidencePackage=1`, label: 'Evidence Package', actionLabel: 'View Package' };
    }

    if (isResubmissionGuidanceAssessmentProcess(process)) {
        return {
            to: `${assessmentLink}?stage=FEEDBACK_PENDING&feedbackStep=GUIDANCE`,
            label: 'Assessment',
            state: {
                assessmentStageTab: 'FEEDBACK_PENDING',
                feedbackStep: 'GUIDANCE'
            }
        };
    }

    if (isInterviewTranscriptAssessmentProcess(process)) {
        return {
            to: `${assessmentLink}?stage=INTERVIEW_MAPPING`,
            label: 'Assessment',
            state: {
                assessmentStageTab: 'INTERVIEW_MAPPING'
            }
        };
    }

    if ([BACKEND_PROCESS_TYPES.UNITS_RELATIONSHIP_MATRIX, BACKEND_PROCESS_TYPES.UNIT_OUTCOME_DETAILS].includes(process?.processType)) {
        const courseLink = getCourseLink(process);
        return courseLink
            ? {
                  to: courseLink,
                  label: 'Course'
              }
            : null;
    }

    if (!assessmentLink) return null;

    return {
        to: assessmentLink,
        label: 'Assessment'
    };
};

const disableRelationshipMatrix = process.env.REACT_APP_DISABLE_MATRIX_COURSE_CREATION;

export default function BackendProcessControl({ allowedPrincipalTypes = ['user', 'candidate'] }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { borderRadius } = useConfig();
    const { isLoggedIn, user } = useAuth();
    const dispatch = useDispatch();
    const { processes, loading, error } = useSelector((state) => state.backendProcess);
    const activeProcesses = useSelector(selectActiveBackendProcesses);
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const canShow = isLoggedIn && allowedPrincipalTypes.includes(user?.principalType || 'user');
    const activeCount = activeProcesses.length;

    const loadProcesses = useCallback(async () => {
        if (!canShow) return;

        try {
            await dispatch(fetchBackendProcesses({ limit: 50 }));
        } catch (loadError) {
            // Slice owns the error state.
        }
    }, [canShow, dispatch]);

    useEffect(() => {
        if (!canShow) return undefined;

        loadProcesses();

        const socket = retainAppSocket();

        const handleSocketConnect = () => {
            loadProcesses();
        };

        const handleBackendProcessUpdated = (process) => {
            const currentProcesses = store.getState()?.backendProcess?.processes || [];
            const existingProcess = currentProcesses.find((item) => item._id === process?._id);
            const previousStatus = existingProcess?.status || '';
            const nextStatus = process?.status || '';
            const processNavigation = getProcessNavigation(process);
            const assessmentCode = String(process.metadata?.assessmentCode || '').trim();
            const assessmentLabel = assessmentCode ? ` (Assessment ${assessmentCode})` : '';

            if (previousStatus !== nextStatus && nextStatus === 'completed') {
                const unavailableFiles =
                    process.processType === BACKEND_PROCESS_TYPES.EVIDENCE_PACKAGE && process.metadata?.missingFileCount;

                console.log('process.displayName', process.displayName);
                console.log('disableRelationshipMatrix', disableRelationshipMatrix);
                if (process.displayName === 'Units Relationship Matrix' && disableRelationshipMatrix === 'true') {
                    dispatch(upsertProcess(process));
                    return;
                }

                enqueueSnackbar(
                    unavailableFiles
                        ? `Evidence package ready with ${unavailableFiles} unavailable files${assessmentLabel}`
                        : `${process.displayName || 'Backend Process'} completed${assessmentLabel}`,
                    {
                        variant: unavailableFiles ? 'warning' : 'success',
                        action: processNavigation
                            ? () => (
                                  <Button
                                      color="inherit"
                                      size="small"
                                      onClick={() => navigate(processNavigation.to, { state: processNavigation.state })}
                                  >
                                      {processNavigation.actionLabel || `Continue ${processNavigation.label}`}
                                  </Button>
                              )
                            : undefined
                    }
                );
            }

            if (previousStatus !== nextStatus && nextStatus === 'failed') {
                const errorMessage =
                    process?.error?.message || `We couldn't complete ${process.displayName || 'the backend process'}. Please try again.`;
                enqueueSnackbar(
                    `${errorMessage}${assessmentLabel}`,
                    {
                        variant: 'error',
                        action: processNavigation
                            ? () => (
                                  <Button
                                      color="inherit"
                                      size="small"
                                      onClick={() => navigate(processNavigation.to, { state: processNavigation.state })}
                                  >
                                      Open {processNavigation.label}
                                  </Button>
                              )
                            : undefined
                    }
                );
            }

            dispatch(upsertProcess(process));
        };

        const handleSocketError = (socketError) => {
            console.error('Backend process socket error', socketError);
        };

        socket.on('connect', handleSocketConnect);
        socket.on('backend-process.updated', handleBackendProcessUpdated);
        socket.on('connect_error', handleSocketError);

        return () => {
            socket.off('connect', handleSocketConnect);
            socket.off('backend-process.updated', handleBackendProcessUpdated);
            socket.off('connect_error', handleSocketError);
            releaseAppSocket();
        };
    }, [canShow, dispatch, loadProcesses, navigate]);

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event?.target)) {
            return;
        }

        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((current) => !current);
    };

    const handleProcessClick = (process) => {
        const processNavigation = getProcessNavigation(process);
        if (!processNavigation?.to) return;

        setOpen(false);
        navigate(processNavigation.to, { state: processNavigation.state });
    };

    const processItems = useMemo(() => processes.slice(0, 20), [processes]);

    if (!canShow) return null;

    /**
     * Show Data base on Process Type
     */
    const showData = (process) => {
        const { metadata } = process;

        if (metadata?.assessmentCode || process?.entityType === 'assessment') {
            return (
                <Typography variant="subtitle2" color="text.secondary">
                    Assessment: {metadata?.assessmentCode || '-'}
                </Typography>
            );
        } else if (
            [BACKEND_PROCESS_TYPES.UNITS_RELATIONSHIP_MATRIX, BACKEND_PROCESS_TYPES.UNIT_OUTCOME_DETAILS].includes(process?.processType)
        ) {
            return (
                <>
                    <Typography variant="subtitle2" color="text.secondary">
                        Course Name: {metadata?.courseName || '-'}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        Unit Codes:{' '}
                        {metadata?.unitCodes?.map((unit, index) => {
                            if (index + 1 !== metadata?.unitCodes?.length) return `${unit}, `;
                            return unit;
                        })}
                    </Typography>
                </>
            );
        }

        return null;
    };

    return (
        <>
            <Box sx={styles.triggerWrap} ref={anchorRef}>
                <Badge
                    color="error"
                    overlap="circular"
                    max={99}
                    badgeContent={activeCount}
                    invisible={!activeCount}
                    sx={styles.triggerBadge}
                >
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                            color: theme.palette.primary.dark,
                            transition: 'all .2s ease-in-out',
                            '&[aria-controls="menu-list-grow"],&:hover': {
                                borderColor: theme.palette.primary.main,
                                background: theme.palette.primary.main,
                                color: theme.palette.primary.light
                            }
                        }}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        title="Notifications"
                        onClick={handleToggle}
                        color="inherit"
                    >
                        <IconDeviceDesktopCog />
                    </Avatar>
                </Badge>
            </Box>

            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions position="top-right" in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    border={false}
                                    elevation={16}
                                    content={false}
                                    boxShadow
                                    shadow={theme.shadows[16]}
                                    sx={styles.panelCard}
                                >
                                    <Box sx={styles.panelHeader}>
                                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                                            <Box>
                                                <Typography variant="h4" className="main-font-b">
                                                    Backend Process
                                                </Typography>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    {activeCount
                                                        ? `${activeCount} active process${activeCount > 1 ? 'es' : ''}`
                                                        : 'No active processes'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>

                                    {error ? (
                                        <Box sx={styles.emptyState}>
                                            <Alert severity="error">{error}</Alert>
                                        </Box>
                                    ) : !processItems.length && !loading ? (
                                        <Box sx={styles.emptyState}>
                                            <Typography variant="h5" className="main-font-b">
                                                No backend processes yet
                                            </Typography>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Long-running tasks will appear here when they start.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={styles.panelBody}>
                                            <List disablePadding>
                                                {processItems.map((process) => {
                                                    const isActive = ACTIVE_PROCESS_STATUSES.includes(process?.status);
                                                    return (
                                                        <ListItemButton
                                                            key={process._id}
                                                            onClick={() => handleProcessClick(process)}
                                                            sx={styles.item(theme, borderRadius, isActive)}
                                                        >
                                                            <Box sx={{ width: '100%' }}>
                                                                <Box sx={styles.itemHeader}>
                                                                    <Typography variant="h5" className="main-font-b">
                                                                        {process.displayName || 'Backend Process'}
                                                                    </Typography>
                                                                    <Chip
                                                                        size="small"
                                                                        label={process.status || 'unknown'}
                                                                        color={getStatusChipColor(process.status)}
                                                                        sx={{ textTransform: 'capitalize' }}
                                                                    />
                                                                </Box>

                                                                <Box sx={styles.itemBody}>
                                                                    {showData(process)}
                                                                    <Typography variant="subtitle2" color="text.secondary">
                                                                        Started: {formatProcessDate(process.startedAt || process.createdAt)}
                                                                    </Typography>
                                                                    <Typography variant="subtitle2" color="text.secondary">
                                                                        Completed: {formatProcessDate(process.completedAt)}
                                                                    </Typography>
                                                                    {process?.error?.message ? (
                                                                        <Typography variant="subtitle2" color="error.main" sx={{ mt: 0.5 }}>
                                                                            {process.error.message}
                                                                        </Typography>
                                                                    ) : null}
                                                                </Box>
                                                            </Box>
                                                        </ListItemButton>
                                                    );
                                                })}
                                            </List>
                                        </Box>
                                    )}
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
}
